import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { styled, keyframes } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../admin/hooks/useTranslation';
import LanguageSelector from '../admin/layout/LanguageSelector';
import LoginModal from '../components/auth/LoginModal';
import RegisterSystem from '../components/RegisterSystem';
import { useAuth } from '../auth/AuthContext';
import { getUserId } from '../utils/auth';
import { BASE_URL } from '../config';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

const floatAnimation = keyframes`
  0% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(10deg); }
  100% { transform: translateY(0) rotate(0deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const EmojiBackground = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100vh',
  zIndex: -1,
  opacity: 0.8,
  fontSize: '28px',
  lineHeight: '1.8',
  whiteSpace: 'pre-wrap',
  overflow: 'hidden',
  pointerEvents: 'none',
  userSelect: 'none',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-around',
  alignContent: 'flex-start',
  padding: '20px',
  '& span': {
    display: 'inline-block',
    margin: '10px',
    animation: `${floatAnimation} ${Math.random() * 10 + 5}s ease-in-out infinite`,
    animationDelay: `${Math.random() * 5}s`,
  },
}));

const Overlay = styled(Box)({
  position: 'relative',
  zIndex: 1,
  minHeight: '100vh',
  backgroundColor: 'rgba(255,255,255,0.85)',
  display: 'flex',
  flexDirection: 'column',
});

const EMOJIS = [
  '💘', '❤️', '🔥', '✨', '🌟', '💫', '💞', '💖', '💓', '💗', '💕', '💝',
  '😊', '🥰', '😍', '🤩', '😘', '💋', '👫', '👭', '👬', '💑', '💏', '💐',
  '🌸', '🌹', '🌺', '🌻', '🌼', '🌷', '💮', '🏵️', '🌱', '🍀', '☘️', '🌿'
];

const generateEmojis = (count: number) => {
  return Array.from({ length: count }, (_, i) => {
    const emoji = EMOJIS[Math.floor(Math.random() * EMOJIS.length)];
    return <span key={i}>{emoji}</span>;
  });
};

const HomePage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { logout, userInfo } = useAuth();
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const fetchPhoto = useCallback(() => {
    const userId = getUserId();
    if (!userId || !userInfo?.profilePhoto) return;
    const url = `${BASE_URL}/object-store/img/get/${userId}/THUMB/${userInfo.profilePhoto}`;
    setPhotoUrl(url);
  }, [userInfo, BASE_URL]);

  // Modal state'leri
  const [loginOpen, setLoginOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      fetchPhoto();
      setFirstName(userInfo?.firstName || null);
    };

    if (userInfo) {
      initialize();
    }
  }, [userInfo, fetchPhoto]);

  // Emoji container ref'i
  const emojiContainerRef = useRef<HTMLDivElement>(null);

  // Emoji animasyonunu yönet
  useEffect(() => {
    const container = emojiContainerRef.current;
    if (!container) return;

    let animationFrameId: number;
    let lastScrollTime = 0;
    const scrollSpeed = 0.2;

    const animate = (time: number) => {
      if (!lastScrollTime) lastScrollTime = time;
      const deltaTime = time - lastScrollTime;
      lastScrollTime = time;

      container.scrollTop = (container.scrollTop + scrollSpeed * deltaTime * 0.05) % container.scrollHeight;
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      setAnchorEl(null);
    } finally {
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileNavigate = () => {
    navigate('/profile');
    handleMenuClose();
  };

  return (
    <>
      <EmojiBackground ref={emojiContainerRef}>
        {generateEmojis(450)}
      </EmojiBackground>

      <Overlay>
        <AppBar position="sticky" color="transparent" elevation={0} sx={{
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid rgba(0,0,0,0.1)',
          py: 1
        }}>
          <Toolbar sx={{
            justifyContent: 'space-between',
            px: { xs: 1, md: 4 },
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 2 }
          }}>
            {/* Logo ve Marka Adı*/}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              order: { xs: 1, sm: 1 }
            }}>
              <Avatar
                src="/logo192.png"
                alt="lowerdash logo"
                sx={{
                  width: { xs: 32, sm: 40, md: 50 },
                  height: { xs: 32, sm: 40, md: 50 },
                  animation: `${pulse} 4s infinite`,
                }}
              />
              <Typography variant="h6" fontWeight="bold" sx={{
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '1.1rem', sm: '1.5rem' }
              }}>
                lowerdash
              </Typography>
            </Box>

            {/* Mobil için sabit navigasyon */}
            {isMobile && !isAuthenticated && (
              <Box sx={{
                order: 3,
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                mt: 1
              }}>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  onClick={() => setLoginOpen(true)}
                  sx={{ flex: 1, mx: 0.5 }}
                >
                  {t('homepage.login.button', 'Login')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  onClick={() => setRegisterOpen(true)}
                  sx={{ flex: 1, mx: 0.5 }}
                >
                  {t('homepage.register.button', 'Sign Up')}
                </Button>
              </Box>
            )}

            {/* Dil seçici ve desktop butonlar  */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                order: { xs: 2, sm: 2 },
                ml: 'auto'
              }}
            >
              
              {!isAuthenticated ? (
                !isMobile && (
                  <>
                    <Button
                      color="primary"
                      onClick={() => setLoginOpen(true)}
                      sx={{ fontWeight: 600 }}
                    >
                      {t('homepage.login.button', 'Login')}
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => setRegisterOpen(true)}
                      sx={{ fontWeight: 600 }}
                    >
                      {t('homepage.register.button', 'Sign Up')}
                    </Button>
                  </>
                )
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {t('homepage.welcome', 'Welcome')}, {firstName}
                  </Typography>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    onClick={handleMenuOpen}
                  >
                    <Avatar
                      src={photoUrl || undefined}
                      alt="Profile"
                      sx={{ width: 32, height: 32 }}
                    />
                    <ArrowDropDownIcon />
                  </Box>
                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleMenuClose}
                    PaperProps={{
                      elevation: 0,
                      sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5,
                        '& .MuiAvatar-root': {
                          width: 32,
                          height: 32,
                          ml: -0.5,
                          mr: 1,
                        },
                        '&:before': {
                          content: '""',
                          display: 'block',
                          position: 'absolute',
                          top: 0,
                          right: 14,
                          width: 10,
                          height: 10,
                          bgcolor: 'background.paper',
                          transform: 'translateY(-50%) rotate(45deg)',
                          zIndex: 0,
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <MenuItem onClick={handleProfileNavigate}>
                      <ListItemIcon>
                        <PersonOutlineIcon fontSize="small" />
                      </ListItemIcon>
                      {t('homepage.profileMenu.goToApp', 'Uygulamaya git')}
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                      <ListItemIcon>
                        <ExitToAppIcon fontSize="small" />
                      </ListItemIcon>
                      {t('homepage.profileMenu.logout', 'Çıkış')}
                    </MenuItem>
                  </Menu>
                </Box>
              )}
              <LanguageSelector />
            </Stack>
          </Toolbar>
        </AppBar>

        <Container
          maxWidth="md"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            py: { xs: 6, md: 10 },
          }}
        >
          {/* Büyük logo - sayfa ortasında  */}
          <Avatar
            src="/logo512.png"
            alt="lowerdash logo"
            sx={{
              width: isMobile ? 120 : 180,
              height: isMobile ? 120 : 180,
              mb: 4,
              animation: `${pulse} 4s infinite`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              border: '4px solid white'
            }}
          />

          <Typography
            variant={isMobile ? 'h4' : 'h2'}
            fontWeight="bold"
            gutterBottom
            sx={{
              mb: 3,
              lineHeight: 1.2,
              animation: `${fadeIn} 0.8s ease-out forwards`,
              opacity: 0,
              animationDelay: '0.2s',
            }}
          >
            {t('homepage.sloganTitle', 'Real People. Real Connections.')}
          </Typography>
          <Typography
            variant={isMobile ? 'body1' : 'h6'}
            color="textSecondary"
            maxWidth="md"
            sx={{
              mt: 1,
              mb: 4,
              animation: `${fadeIn} 0.8s ease-out forwards`,
              opacity: 0,
              animationDelay: '0.4s',
            }}
          >
            {t(
              'homepage.sloganDescription',
              'lowerdash helps you meet genuine people looking for meaningful relationships. Swipe, match and connect in a way that feels real — because love deserves more than just algorithms.'
            )}
          </Typography>

          <Stack
            spacing={2}
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="center"
            sx={{
              animation: `${fadeIn} 0.8s ease-out forwards`,
              opacity: 0,
              animationDelay: '0.6s',
            }}
          >
            <Button
              variant="contained"
              size="large"
              color="primary"
              onClick={() => setRegisterOpen(true)}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                }
              }}
            >
              {t('homepage.ctaPrimary', 'Start Your Journey')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => setLoginOpen(true)}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderWidth: '2px',
                '&:hover': {
                  borderWidth: '2px',
                }
              }}
            >
              {t('homepage.ctaSecondary', 'Already a member? Login')}
            </Button>
          </Stack>
        </Container>

        {/* Footer (mevcut yapı korundu) */}
        <Box
          textAlign="center"
          py={2}
          px={2}
          sx={{
            position: { xs: 'fixed', sm: 'static' },
            bottom: 0,
            left: 0,
            right: 0,
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(8px)',
            borderTop: '1px solid rgba(0,0,0,0.1)',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Stack direction="row" spacing={3} justifyContent="center" mb={1}>
            <IconButton
              href="https://instagram.com/"
              target="_blank"
              size="small"
            >
              <InstagramIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
            <IconButton
              href="https://tiktok.com/"
              target="_blank"
              size="small"
            >
              <MusicNoteIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
            <IconButton
              href="https://youtube.com/@lowerdash"
              target="_blank"
              size="small"
            >
              <YouTubeIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Stack>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              src="/logo192.png"
              alt="lowerdash logo"
              sx={{ width: 20, height: 20 }}
            />
            <Typography variant="caption" color="textSecondary">
              © 2025 lowerdash
            </Typography>
          </Box>
        </Box>
      </Overlay>

      {/* Login Modal */}
      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onSwitchToRegister={() => {
          setLoginOpen(false);
          setRegisterOpen(true);
        }}
      />

      {/* Register Modal */}
      <RegisterSystem
        mode="modal"
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
      />
    </>
  );
};

export default HomePage;